import {tolgeeInstance} from 'tolgee';

import {AuthService} from '~api/auth.service';
import {store} from '~redux/index';

export interface AskAiWidgetController {
  version: string;
  mount: (container: HTMLElement) => unknown;
  unmount: () => void;
  dispose: () => void;
}

interface AskAiWidgetModule {
  initAskAiWidget: (options: {
    host: {
      store: {getState: () => unknown};
      getToken: () => string | null;
      getLanguage: () => string | undefined;
    };
    apiUrl: string;
    apiToken?: string;
    debug?: boolean;
  }) => AskAiWidgetController;
}

let controllerPromise: Promise<AskAiWidgetController | null> | null = null;

const ALLOWED_WIDGET_HOST_SUFFIXES = ['.maddox.ai', '.azurecontainerapps.io'];

function isAllowedWidgetOrigin(url: URL): boolean {
  if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
    return true;
  }
  return url.protocol === 'https:' && ALLOWED_WIDGET_HOST_SUFFIXES.some((suffix) => url.hostname.endsWith(suffix));
}

export function getAskAiWidgetBaseUrl(): string | null {
  const raw = import.meta.env.VITE_ASK_AI_WIDGET_URL as string | undefined;
  if (!raw) {
    return null;
  }
  try {
    const url = new URL(raw);
    if (!isAllowedWidgetOrigin(url)) {
      console.error('[ask-ai] widget URL rejected by origin allowlist:', url.origin);
      return null;
    }
  } catch {
    console.error('[ask-ai] invalid widget URL:', raw);
    return null;
  }
  return raw.replace(/\/+$/, '');
}

export function loadAskAiWidget(): Promise<AskAiWidgetController | null> {
  if (controllerPromise) {
    return controllerPromise;
  }

  const baseUrl = getAskAiWidgetBaseUrl();
  if (!baseUrl) {
    return Promise.resolve(null);
  }

  controllerPromise = import(/* @vite-ignore */ `${baseUrl}/widget/latest.js`)
    .then((module: AskAiWidgetModule) =>
      module.initAskAiWidget({
        host: {
          store: {getState: store.getState},
          getToken: AuthService.getToken,
          getLanguage: () => tolgeeInstance.getLanguage(),
        },
        apiUrl: baseUrl,
      }),
    )
    .catch((error) => {
      console.error('[ask-ai] widget failed to load', error);
      controllerPromise = null;
      return null;
    });

  return controllerPromise;
}

export function disposeAskAiWidget(): void {
  const promise = controllerPromise;
  controllerPromise = null;
  void promise?.then((controller) => controller?.dispose());
}
